/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useMemo, useRef, type FC } from "react";
import { canUseDOM } from "history/DOMUtils";
import spinStyles from "./SpinContainer.module.scss";
import styles from "./SpinContainerXca.module.scss";
import { useCarConfigSelector } from "../../../../redux/store";
import { useCarConfigCCISNormalization, useCarIds } from "../../../../hooks/useAphCCISNormalization";
import { getModelToken } from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import Debug from "../../../../../../../common-deprecated/Debug";
import { CarConfigConfigureSection } from "../../../../utils/types/SectionTypes";

type ImageConfigType = {
    modelId: string;
    gradeId?: string;
    bodyTypeId?: string;
    carId: string;
    colorCode: string;
    upholsteryCode: string;
    packIds: string[];
    accessoryIds: string[];
    optionIds: string[];
    modelToken?: string; // ModelToken is not always present. In that case modelId and carId is used
    steeringPosition?: "LHD" | "RHD";
};

type PanelType = "exterior" | "interior";

const SpinContainerXca: FC = () => {
    if (!canUseDOM) return null;

    // ----------------------------------------------------------------------
    // Logic to sync data to the xca module
    // ----------------------------------------------------------------------

    const carIds = useCarIds();
    const configurables = useCarConfigCCISNormalization();
    const modelToken = useCarConfigSelector((state) => getModelToken(state.commonSettings, carIds.modelId) || "");
    const activeSection = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const country = useCarConfigSelector((state) => state.commonSettings.country);

    const data = useMemo<ImageConfigType>(
        () => ({
            ...carIds,
            ...configurables,
            modelToken,
            colorCode: configurables.colorCode || "",
            upholsteryCode: configurables.upholsteryCode || "",
            packIds: configurables.packIds || [],
            accessoryIds: configurables.accessoryIds || [],
            optionIds: configurables.optionIds || [],
            steeringPosition: country === "gb" ? "LHD" : "RHD",
        }),
        [carIds, configurables, country, modelToken],
    );

    // We use a ref + useEffects to ensure the xca link works in whichever order the components are rendered
    const updateConfig = useRef<(data: ImageConfigType) => void>();
    const updatePanel = useRef<(data: PanelType) => void>();

    useEffect(() => {
        const setupXca = (): void => {
            if (!window.XCA) {
                Debug.debug("XCA not found, waiting for it to be available");
                return;
            }
            const xcaComponent = window.XCA.component("cc.aphcarconfig.SpinContainer");
            updateConfig.current = xcaComponent?.getUpdateFn("cc.basket.config", data);
            updateConfig.current?.(data);

            updatePanel.current = xcaComponent?.getUpdateFn("cc.carviewer.panel");
        };

        setupXca();
        const interval = setInterval(() => {
            setupXca();
            clearInterval(interval);
        }, 1000);

        return () => clearInterval(interval);
        // Ignore data as this is only used to set the initial data in case the channel was not created yet
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!updateConfig.current) Debug.debug("XCA component not found, didn't render new data", data);
        else updateConfig.current(data);
    }, [data]);

    useEffect(() => {
        if (!updatePanel.current) return;
        const panel = activeSection === CarConfigConfigureSection.Upholstery ? "interior" : "exterior";
        updatePanel.current(panel);
    }, [activeSection]);

    // ----------------------------------------------------------------------
    // Logic to find xcaModule on the page and move it inside our component
    // ----------------------------------------------------------------------

    const xcaModuleId = useCarConfigSelector((state) => state.carConfigSettings.xcaModules?.["car-viewer"]);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!xcaModuleId || !ref.current) return;
        const element = document.querySelector(`#${xcaModuleId}`);
        if (!element) {
            Debug.warn("Could not find XCA module with id:", xcaModuleId);
            return;
        }
        ref.current.appendChild(element);
    }, [xcaModuleId]);

    // ----------------------------------------------------------------------
    // Render wrapper for xca module
    // ----------------------------------------------------------------------

    const carInitialized = useCarConfigSelector((state) => state.car.initialized);
    const spinnerIsInactive =
        !carInitialized ||
        activeSection === CarConfigConfigureSection.Summary ||
        activeSection === CarConfigConfigureSection.Charging;

    return useMemo(
        () => (
            <section className={`${spinStyles.spin} retrofit-enabled`}>
                <div
                    className={`${spinStyles["spin-container"]} ${styles["xca-spin-container"]} ${spinnerIsInactive ? spinStyles.faded : spinStyles.active}`}
                    ref={ref}
                />
            </section>
        ),
        [spinnerIsInactive],
    );
};

export default SpinContainerXca;
